import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CarePlanAdherenceBanner from "../../components/CarePlanAdherenceBanner";
import CarePlanTab from "../../components/care-plan/CarePlanTab";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tab,
  Tabs,
  Avatar,
  Divider,
  Grid
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import patientService from "../../services/patientService";
import DemographicsTab from "./tabs/DemographicsTab";
import MedicalHistoryTab from "./tabs/MedicalHistoryTab";
import MedicationsTab from "./tabs/MedicationsTab";
import AllergiesTab from "./tabs/AllergiesTab";
import VitalsTab from "./tabs/VitalsTab";
//icons
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MedicationIcon from "@mui/icons-material/Medication";
import WarningIcon from "@mui/icons-material/Warning"
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart"
import ScienceIcon from "@mui/icons-material/Science"
import EpicLabResults from "../../components/EpicLabResults"



const PatientProfilePage = () => {
  const { patientId } = useParams(); // Changed from { id }
  const navigate = useNavigate();

  // ✅ Removed isNewPatient - this page is ONLY for viewing existing patients

  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true); // ✅ Start with true since we always load
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [patientData, setPatientData] = useState({
    demographics: {
      firstName: "",
      middleName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: "United States",
      primaryConcern: "",
      ckdStage: "",
      diagnosisDate: "",
      dialysisModality: "",
    },
    medicalHistory: {
      pastDiagnoses: [],
      pastSurgeries: [],
      familyHistory: [],
    },
    medications: [],
    allergies: [],
    vitals: [],
  });

  // ✅ Load patient data - ALWAYS load since this is view-only page
  const loadPatientData = useCallback(async () => {
    if (!patientId) {
      console.error("❌ No patient ID provided");
      navigate("/patients");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("📡 Loading patient with ID:", patientId);
      const response = await patientService.getPatientById(patientId);

      if (response.success) {
        const backendData = response.data;
        console.log("✅ Patient data loaded:", backendData);

        // ✅ Helper: Parse medications string array to objects
        const parseMedications = (medications) => {
          if (!medications || !Array.isArray(medications)) return [];

          return medications.map((med) => {
            if (typeof med === "string") {
              const parts = med.split(" - ").map((p) => p.trim());
              return {
                name: parts[0] || "",
                dosage: parts[1] || "",
                frequency: parts[2] || "",
                startDate: "",
                notes: "",
              };
            }
            return med;
          });
        };

        // ✅ Helper: Parse allergies string array to objects
        const parseAllergies = (allergies) => {
          if (!allergies || !Array.isArray(allergies)) return [];

          return allergies.map((allergy) => {
            if (typeof allergy === "string") {
              return {
                allergen: allergy,
                reaction: "",
                severity: "",
                notes: "",
              };
            }
            return allergy;
          });
        };

        // ✅ Helper: Parse medical conditions to pastDiagnoses
        const parseMedicalConditions = (conditions) => {
          if (!conditions || !Array.isArray(conditions)) return [];
          return conditions.map((condition) => {
            if (typeof condition === 'object') return condition.condition || '';
            return condition;
          }).filter(c => c);
        };
        const mappedData = {
          demographics: {
            firstName: backendData.firstName || "",
            middleName: backendData.middleName || "",
            lastName: backendData.lastName || "",
            dateOfBirth: backendData.dateOfBirth || "",
            gender: backendData.gender || "",
            email: backendData.email || "",
            phone: backendData.phoneNumber || "",
            address: backendData.streetAddress || "",
            city: backendData.city || "",
            state: backendData.state || "",
            zip: backendData.zipCode || "",
            country: backendData.country || "United States",
            primaryConcern: backendData.notes || "",
            ckdStage: backendData.ckdStage || "",
            diagnosisDate: backendData.diagnosisDate || "",
            dialysisModality: backendData.dialysisModality || "",
          },
          medicalHistory: {
            pastDiagnoses: parseMedicalConditions(
              backendData.medicalConditions || backendData.pastDiagnoses || []
            ),
            pastSurgeries: backendData.pastSurgeries || [],
            familyHistory: backendData.familyHistory || [],
          },
          medications: parseMedications(
            backendData.currentMedications || backendData.medications
          ),
          allergies: parseAllergies(backendData.allergies),
          vitals: backendData.vitals || [],

          consents: {
            hipaaConsent: backendData.hipaaConsent || false,
            termsAccepted: backendData.termsAccepted || false,
            privacyPolicyAccepted: backendData.privacyPolicyAccepted || false,
            researchConsent: backendData.researchConsent || false,
            dataSharingConsent: backendData.dataSharingConsent || false,
          },

        };

        console.log("✅ Mapped data:", mappedData);
        setPatientData(mappedData);
      } else {
        setError("Failed to load patient data");
      }
    } catch (err) {
      console.error("❌ Error loading patient:", err);
      setError("Failed to load patient data: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [patientId, navigate]);

  // ✅ Small, clean useEffect
  useEffect(() => {
    console.log("🔍 PatientProfilePage mounted - Loading patient:", patientId);
    loadPatientData();
  }, [loadPatientData]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const updatePatientData = (section, data) => {
    setPatientData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // ✅ Convert complex objects to simple arrays that backend expects
      const backendData = {
        // Basic demographics
        firstName: patientData.demographics.firstName,
        middleName: patientData.demographics.middleName,
        lastName: patientData.demographics.lastName,
        dateOfBirth: patientData.demographics.dateOfBirth,
        gender: patientData.demographics.gender,
        email: patientData.demographics.email,
        phoneNumber: patientData.demographics.phone,

        // Address
        streetAddress: patientData.demographics.address,
        city: patientData.demographics.city,
        state: patientData.demographics.state,
        zipCode: patientData.demographics.zip,
        country: patientData.demographics.country,

        // Medical info
        ckdStage: patientData.demographics.ckdStage,
        diagnosisDate: patientData.demographics.diagnosisDate,
        notes: patientData.demographics.primaryConcern,

        // ✅ Convert medications array of objects to simple string array
        currentMedications: patientData.medications
          .map((med) =>
            med.name
              ? `${med.name}${med.dosage ? " - " + med.dosage : ""}${med.frequency ? " - " + med.frequency : ""
              }`
              : ""
          )
          .filter((m) => m),

        // ✅ Convert allergies array of objects to simple string array
        allergies: patientData.allergies
          .map((allergy) => allergy.allergen || "")
          .filter((a) => a),

        // ✅ Convert medical conditions from pastDiagnoses
        medicalConditions: patientData.medicalHistory.pastDiagnoses
          .map((d) => d.condition || "")
          .filter((c) => c),
        hipaaConsent: patientData.consents?.hipaaConsent || true,
        termsAccepted: patientData.consents?.termsAccepted || true,
        privacyPolicyAccepted: patientData.consents?.privacyPolicyAccepted || true,
        researchConsent: patientData.consents?.researchConsent || false,
        dataSharingConsent: patientData.consents?.dataSharingConsent || false,
      };

      console.log("📤 Updating patient data:", backendData);

      const response = await patientService.updatePatient(patientId, backendData);

      if (response.success) {
        setSuccess("Patient updated successfully!");
        setTimeout(() => {
          loadPatientData(); // Reload to show latest data
        }, 1000);
      } else {
        setError(response.message || "Failed to update patient");
      }
    } catch (err) {
      console.error("❌ Error saving patient:", err);
      setError("Failed to save patient: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/patients")}
        >
          Back to Patients
        </Button>
      </Box>

      {/* Patient Info Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Left Side - Avatar and Name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: "rgba(255,255,255,0.3)",
                fontSize: "1.5rem",
              }}
            >
              {patientData.demographics.firstName?.[0] || "P"}
              {patientData.demographics.lastName?.[0] || ""}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {patientData.demographics.firstName}{" "}
                {patientData.demographics.lastName}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                {patientData.demographics.ckdStage && (
                  <Chip
                    label={patientData.demographics.ckdStage}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                    size="small"
                  />
                )}
                <Chip
                  label={`ID: ${patientId}`}
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                  size="small"
                />
              </Box>
            </Box>
          </Box>

          {/* Right Side - Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<CalendarTodayIcon />}
              onClick={() =>
                navigate(`/patients/${patientId}/daily-intake/history`)
              }
              sx={{
                bgcolor: "white",
                color: "primary.main",
                fontWeight: "bold",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
            >
              View Daily Logs
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/patients/${patientId}/daily-intake`)}
              sx={{
                bgcolor: "white",
                color: "primary.main",
                fontWeight: "bold",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
              }}
            >
              Today's Log
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: "white",
                color: "success.main",
                fontWeight: "bold",
                "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                "&:disabled": { bgcolor: "rgba(255,255,255,0.5)" },
              }}
            >
              {saving ? "Saving..." : "Save All"}
            </Button>
          </Box>
        </Box>
      </Paper>
             <CarePlanAdherenceBanner patientId={patientId} audience="provider" /> 
      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Tabs - Keep the rest exactly as it was */}
      <Paper elevation={2}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 72,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 500,
            },
          }}
        >
          <Tab
            icon={<PersonIcon />}
            label="Demographics"
            iconPosition="start"
          />
          <Tab
            icon={<MedicalServicesIcon />}
            label="Medical History"
            iconPosition="start"
          />
          <Tab
            icon={<MedicationIcon />}
            label="Medications"
            iconPosition="start"
          />
          <Tab icon={<WarningIcon />} label="Allergies" iconPosition="start" />
          <Tab
            icon={<MonitorHeartIcon />}
            label="Vitals"
            iconPosition="start"
          />
          <Tab
            icon={<ScienceIcon />}
            label="Lab Results"
            iconPosition="start"
          />
          <Tab icon={<AssignmentTurnedInIcon />} label="Care Plan" iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {currentTab === 0 && (
            <Box>
              <DemographicsTab
                data={patientData.demographics}
                onChange={(data) => updatePatientData("demographics", data)}
              />
              <Divider sx={{ mt: 4, mb: 3 }} />
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Consent & Privacy
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'HIPAA Consent', value: patientData.consents?.hipaaConsent },
                  { label: 'Terms & Conditions', value: patientData.consents?.termsAccepted },
                  { label: 'Privacy Policy', value: patientData.consents?.privacyPolicyAccepted },
                  { label: 'Research Consent', value: patientData.consents?.researchConsent },
                  { label: 'Data Sharing', value: patientData.consents?.dataSharingConsent },
                ].map(({ label, value }) => (
                  <Grid item xs={12} sm={6} md={4} key={label}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        bgcolor: value ? 'success.main' : 'error.light',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: 16, fontWeight: 'bold', flexShrink: 0
                      }}>
                        {value ? '✓' : '✗'}
                      </Box>
                      <Typography variant="body2">
                        <strong>{label}:</strong> {value ? 'Accepted' : 'Not accepted'}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          {currentTab === 1 && (
            <MedicalHistoryTab
              data={patientData.medicalHistory}
              onChange={(data) => updatePatientData("medicalHistory", data)}
            />
          )}
          {currentTab === 2 && (
            <MedicationsTab
              data={patientData.medications}
              onChange={(data) => updatePatientData("medications", data)}
            />
          )}
          {currentTab === 3 && (
            <AllergiesTab
              data={patientData.allergies}
              onChange={(data) => updatePatientData("allergies", data)}
            />
          )}
          {currentTab === 4 && (
            <VitalsTab
              data={patientData.vitals}
              onChange={(data) => updatePatientData("vitals", data)}
            />
          )}
          {currentTab === 6 && <CarePlanTab patientId={patientId} />}
          {currentTab === 5 && (
            <EpicLabResults patientId={patientId} />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default PatientProfilePage;